import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FooterEl = styled.footer`
  background: #060606;
  border-top: 1px solid #1A1A1A;
  padding: 3.5rem 2rem 2rem;

  @media (max-width: 768px) { padding: 3rem 1.25rem 1.75rem; }
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid #161616;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Brand = styled.div``;

const LogoImg = styled.img`
  height: 42px;
  width: auto;
  background: #fff;
  border-radius: 7px;
  padding: 4px 9px;
  margin-bottom: 1.2rem;
`;

const Tagline = styled.p`
  font-size: 0.87rem;
  color: #555;
  line-height: 1.7;
  max-width: 260px;
`;

const Col = styled.div``;

const ColTitle = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #fff;
  margin-bottom: 1.2rem;
`;

const ColLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ColLink = styled.a`
  font-size: 0.85rem;
  color: #555;
  transition: color 0.2s ease;
  cursor: pointer;
  word-break: break-all;

  &:hover { color: #F47920; }
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 2rem;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const Copy = styled.div`
  font-size: 0.78rem;
  color: #3A3A3A;
`;

const OrangeDot = styled.span`
  color: #F47920;
`;

const Socials = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const SocialLink = styled(motion.a)`
  width: 36px;
  height: 36px;
  border: 1px solid #1E1E1E;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;

  svg { width: 16px; height: 16px; }

  &:hover {
    border-color: #F47920;
    color: #F47920;
  }
`;

export default function Footer() {
  return (
    <FooterEl>
      <Inner>
        <Top>
          <Brand>
            <LogoImg src="/img/logo/logo.jpeg" alt="Carnes Fereima" />
            <Tagline>
              Más de 20 años transformando esfuerzo y dedicación en productos de
              la más alta calidad desde San Juan de los Lagos, Jalisco.
            </Tagline>
          </Brand>

          <Col>
            <ColTitle>Navegación</ColTitle>
            <ColLinks>
              <ColLink href="#inicio">Inicio</ColLink>
              <ColLink href="#nosotros">Nosotros</ColLink>
              <ColLink href="#proceso">Proceso</ColLink>
              <ColLink href="#productos">Productos</ColLink>
              <ColLink href="#contacto">Contacto</ColLink>
            </ColLinks>
          </Col>

          <Col>
            <ColTitle>Productos</ColTitle>
            <ColLinks>
              <ColLink href="#productos">Cortes Premium</ColLink>
              <ColLink href="#productos">Res / Novillo</ColLink>
              <ColLink href="#productos">Arracheras</ColLink>
              <ColLink href="#productos">Cerdo</ColLink>
              <ColLink href="#productos">Productos Terminados</ColLink>
            </ColLinks>
          </Col>

          <Col>
            <ColTitle>Contacto</ColTitle>
            <ColLinks>
              <ColLink href="tel:3951145320">395 114 53 20</ColLink>
              <ColLink href="mailto:administracion@carnesfereima.com">administracion@carnesfereima.com</ColLink>
              <ColLink href="mailto:carnesfereima@gmail.com">carnesfereima@gmail.com</ColLink>
              <ColLink as="div" style={{ cursor: 'default', color: '#3A3A3A' }}>
                Rancho los Aguirre #2<br />San Juan de los Lagos, Jal.
              </ColLink>
            </ColLinks>
          </Col>
        </Top>

        <Bottom>
          <Copy>
            © {new Date().getFullYear()} <OrangeDot>Carnes Fereima SA de CV</OrangeDot>. Todos los derechos reservados.
          </Copy>
          <Socials>
            <SocialLink
              href="https://wa.me/523951145320"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.124 1.532 5.862L0 24l6.337-1.501A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.584-.5-5.08-1.374l-.362-.214-3.763.891.936-3.667-.237-.375A9.954 9.954 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
              </svg>
            </SocialLink>
            <SocialLink
              href="mailto:carnesfereima@gmail.com"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </SocialLink>
            <SocialLink
              href="https://www.instagram.com/carne_sfereima"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </SocialLink>
            <SocialLink
              href="https://www.facebook.com/CarnesFereima"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </SocialLink>
          </Socials>
        </Bottom>
      </Inner>
    </FooterEl>
  );
}
